import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { AuthProvider } from "./context/AuthContext";
import { PostHogProvider } from "posthog-js/react";
import { PostHog } from "posthog-js";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";

// Runtime accessibility checking in development
if (import.meta.env.DEV) {
  import("@axe-core/react")
    .then((axe) => {
      // Initialize axe-core with default settings
      // Reports accessibility violations in the browser console
      axe.default(React, ReactDOM, 1000);
    })
    .catch(() => {
      // Silently fail if axe-core cannot be loaded
    });
}

// PostHog configuration
const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY || "";
const posthogHost =
  import.meta.env.VITE_PUBLIC_POSTHOG_HOST || "https://app.posthog.com";

const posthogOptions = {
  api_host: posthogHost,
  capture_pageview: true,
  // Additional options for development to prevent tracking in local dev
  loaded: (ph: PostHog) => {
    if (import.meta.env.DEV) {
      // Disable capturing in development
      ph.opt_out_capturing();
    }
    // Remember which email brought a player back (like the win-back email),
    // so their feedback and games can be told apart from everyone else's.
    const campaign = new URLSearchParams(window.location.search).get(
      "utm_campaign"
    );
    if (campaign) ph.register({ returned_via: campaign.slice(0, 64) });
  },
};

const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY || "";

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

// Only wrap with GoogleReCaptchaProvider if site key is configured
const AppWithProviders = recaptchaSiteKey ? (
  <GoogleReCaptchaProvider
    reCaptchaKey={recaptchaSiteKey}
    scriptProps={{
      async: false,
      defer: false,
      appendTo: "head",
      nonce: undefined,
    }}
  >
    <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
      <AuthProvider>
        <App />
      </AuthProvider>
    </PostHogProvider>
  </GoogleReCaptchaProvider>
) : (
  <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
    <AuthProvider>
      <App />
    </AuthProvider>
  </PostHogProvider>
);

root.render(<React.StrictMode>{AppWithProviders}</React.StrictMode>);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
